<template>
  <div class="pa-0">
    <v-row class="reporting-tab-row ma-0">
      <v-col class="reporting-tab-scope h-100 pa-0">
        <v-tabs hide-slider class="reporting-tabs" height="37">
          <!-- The 'exact' prop is needed here to ensure that the correct active tab is selected
              This ensures that we can have both the parent and child routes in the same set of tabs. -->
          <v-tab
            v-for="item in tabs"
            :key="item.key"
            class="reporting-tabs__tab"
            :ripple="false"
            :disabled="!pagesPermissions[item.permission]"
            :to="createDetailedReportRoute(item.routeName)"
            :replace="true"
            exact
            :title="t(item.translationKey.subtitle)"
          >
            {{ t(item.translationKey.title) }}
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { sortBy, filter, map, get } from 'lodash';
import ReportingSections from '@enums/reporting-sections';
import tabEnum from '@/utils/tabs/reporting-tabs-enum';
import { useFeature } from '@/composables/use-feature';
import { usePermission } from '@/composables/use-permission';
import { useInternationalization } from '@/composables/use-internationalization';

const {
  canViewReportingPage,
  canViewStatsReport,
  canViewRelativeShareReport,
  canViewPriceLadderReport,
  canViewComparisonWaterfallReport,
  canViewProductReport,
} = usePermission();
const { clientConfig } = useFeature();
const route = useRoute();
const { t } = useInternationalization();

const tabs = computed(() => {
  return sortBy(
    filter(
      map(tabEnum.reportingTabs, tab => {
        tab.isEnabled = tab.featureFlag
          ? get(clientConfig, `features.${tab.featureFlag}`, true)
          : true;
        return tab;
      }),
      tab => tab.isEnabled
    ),
    'position'
  );
});

const pagesPermissions = computed(() => {
  return {
    [ReportingSections.overview]: canViewReportingPage.value,
    [ReportingSections.stats]: canViewStatsReport.value,
    [ReportingSections.relativeShare]: canViewRelativeShareReport.value,
    [ReportingSections.comparisonWaterfall]: canViewComparisonWaterfallReport.value,
    [ReportingSections.priceLadder]: canViewPriceLadderReport.value,
    [ReportingSections.product]: canViewProductReport.value,
  };
});

const createDetailedReportRoute = view => {
  return {
    path: `/reporting-main/${view}`,
    query: route.query,
  };
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.reporting-tab-row {
  height: 100%;
}
.reporting-tabs {
  &__tab {
    color: $reporting-tab-text-color !important;
    border: 0.3px solid $assortment-tab-divider-colour;
    border-left: 3px solid $assortment-tab-divider-colour;
    border-bottom: 1px solid $assortment-tab-divider-colour;
    margin-bottom: -1px;
    font-weight: 900;
    height: auto;

    &.v-tab--selected {
      background: $assortment-active-tab-colour;
      border-top: 1px solid $assortment-tab-divider-colour;
      border-left: 3px solid $reporting-section-active-border;
      border-bottom: none;
    }

    &:first-child:not(.tab-last-updated) {
      margin-left: 0px !important;
    }
  }

  .v-slide-group__content :first-child:not(.tab-last-updated) {
    margin-left: 0px !important;
  }
}

.reporting-tab-scope > .reporting-tabs.theme--light.v-tabs > .v-slide-group__container {
  background-color: inherit !important;
}

.reporting-tab-row
  > .reporting-tab-scope
  > .v-tabs
  > .v-slide-group__container
  > .v-slide-group__content {
  border-bottom: 1px solid $assortment-tab-divider-colour;
}
</style>
